// libs/create-transactions-modal/src/lib/services/modal-position.service.ts
import { Injectable } from '@angular/core'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  RootState,
  selectActiveWorkspace,
  updateModalPositions,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { take } from 'rxjs/operators'
import { UserPreferencesService } from './user-preferences.service'

@Injectable({
  providedIn: 'root',
})
export class ModalPositionService {
  constructor(
    private readonly store: Store<RootState>,
    private readonly preferences: UserPreferencesService,
  ) {}

  async getModalPosition(
    modalIdentifier: string,
  ): Promise<{ top: string; left: string } | undefined> {
    const saveEnabled = UserPreferencesService.getSaveModalPositions()

    // If disabled, return undefined to use default position
    if (!saveEnabled) {
      return undefined
    }
    // Get the workspace state
    const activeWorkspace = await this.store
      .select(selectActiveWorkspace)
      .pipe(take(1))
      .toPromise()

    // Check if modal positions exist for this modal
    if (activeWorkspace?.workspace.modalPositions?.[modalIdentifier]) {
      return activeWorkspace.workspace.modalPositions[modalIdentifier]
    }
    return undefined
  }

  async saveModalPosition(
    modalIdentifier: string,
    modalDialog: HTMLElement,
  ): Promise<void> {
    const saveEnabled = UserPreferencesService.getSaveModalPositions()

    // If disabled, return undefined to use default position
    if (!saveEnabled) {
      return undefined
    }
    const position = this.calculatePositionToSave(modalDialog)
    const activeWorkspace = await this.store
      .select(selectActiveWorkspace)
      .pipe(take(1))
      .toPromise()

    if (!activeWorkspace) {
      console.error('No active workspace found')
      return
    }

    // Check if modal positions exist for this modal
    if (
      activeWorkspace?.workspace.modalPositions?.[modalIdentifier] &&
      activeWorkspace?.workspace.modalPositions?.[modalIdentifier].top ==
        position.top &&
      activeWorkspace?.workspace.modalPositions?.[modalIdentifier].left ==
        position.left
    ) {
      return
    }

    // Create updated modalPositions
    const updatedModalPositions = {
      ...(activeWorkspace.workspace.modalPositions || {}),
      [modalIdentifier]: position,
    }

    // Dispatch action to update the modal positions in the workspace
    this.store.dispatch(updateModalPositions(updatedModalPositions))
  }

  calculatePositionToSave(modalDialog: HTMLElement) {
    // getBoundingClientRect and window.inner* report viewport space, which the UI
    // scale zooms; style.top/left are in the element's own space, which it does
    // not. Normalise the first two into the second before combining them, or the
    // centred offset returned below lands short by the scale. The off-screen test
    // survives unnormalised — effectiveTop reduces to modalRect.top once
    // currentTop cancels — but the offset does not.
    const scale = this.preferences.scaleFactor

    // Get the current position values
    const currentTop = modalDialog.style.top
      ? parseFloat(modalDialog.style.top)
      : 0
    const currentLeft = modalDialog.style.left
      ? parseFloat(modalDialog.style.left)
      : 0

    // Get the modal's dimensions
    const clientRect = modalDialog.getBoundingClientRect()
    const modalRect = {
      top: clientRect.top / scale,
      left: clientRect.left / scale,
      width: clientRect.width / scale,
      height: clientRect.height / scale,
    }

    // Get the modal's original position (before any relative positioning was applied)
    // This is the position in the document flow
    const originalPosition = {
      top: modalRect.top - currentTop,
      left: modalRect.left - currentLeft,
    }

    // Get window dimensions
    const windowWidth = window.innerWidth / scale
    const windowHeight = window.innerHeight / scale

    // Calculate where the modal would be with its relative positioning
    const effectiveTop = originalPosition.top + currentTop
    const effectiveLeft = originalPosition.left + currentLeft
    const effectiveRight = effectiveLeft + modalRect.width
    const effectiveBottom = effectiveTop + modalRect.height

    // Check if the modal would be partially off-screen
    const isPartiallyOffScreen =
      effectiveTop < 0 ||
      effectiveLeft < 0 ||
      effectiveRight > windowWidth ||
      effectiveBottom > windowHeight

    if (isPartiallyOffScreen) {
      // Calculate a centered position
      // We need to calculate what relative values would center the modal

      // Center coordinates in the viewport
      const centerTop = Math.max(0, (windowHeight - modalRect.height) / 2)
      const centerLeft = Math.max(0, (windowWidth - modalRect.width) / 2)

      // Calculate relative offsets that would place the modal at the center
      const relativeTop = centerTop - originalPosition.top
      const relativeLeft = centerLeft - originalPosition.left

      return {
        top: `${relativeTop}px`,
        left: `${relativeLeft}px`,
      }
    } else {
      // Use current position
      return {
        top: modalDialog.style.top || '0px',
        left: modalDialog.style.left || '0px',
      }
    }
  }

  handleModalPositionSetup(uniqueId: string, compKey: string) {
    this.getModalPosition(compKey).then(position => {
      if (!position) return
      const modalDialog = document.querySelector(`.${uniqueId}  .modal-dialog`)
      if (modalDialog instanceof HTMLElement) {
        // Apply positioning styles
        modalDialog.style.top = position.top
        modalDialog.style.left = position.left
      }
    })
  }

  handleModalPositionSaveOnDismiss(uniqueId: string, compKey: string): boolean {
    const modalDialog = document.querySelector(`.${uniqueId} .modal-dialog`)
    if (modalDialog instanceof HTMLElement) {
      this.saveModalPosition(compKey, modalDialog)
    }
    return true
  }
}
